import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";

const empty = { name: "", monthly_price: 0, currency: "USD", project_limit: 1, user_limit: 5,
  storage_limit_gb: 10, scene_limit: 30, entity_limit: 500, iframe_enabled: true,
  analytics_enabled: true, lead_form_enabled: true, notes: "" };

export default function AdminPackages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const r = await api.get("/admin/packages");
      setItems(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") {
        await api.post("/admin/packages", form);
        toast.success("Package created");
      } else {
        await api.put(`/admin/packages/${editing}`, form);
        toast.success("Updated");
      }
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${id}`);
    load();
  };

  return (
    <div className="px-8 py-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Studio</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Subscription packages</h1>
        </div>
        <button onClick={() => { setEditing("new"); setForm(empty); }} data-testid="add-package-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700">
          <Plus className="h-4 w-4" /> New package
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="text-sm text-zinc-500" data-testid="empty-packages">No packages yet.</div>}
        {items.map(p => (
          <div key={p.id} className="rounded-xl border border-zinc-200 bg-white p-5" data-testid={`pkg-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{p.currency} {p.monthly_price} / month</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(p.id); setForm({ ...empty, ...p }); }} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-pkg-${p.id}`}><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => del(p.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" data-testid={`del-pkg-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-y-2 text-xs">
              <div className="text-zinc-500">Projects</div><div>{p.project_limit}</div>
              <div className="text-zinc-500">Scenes</div><div>{p.scene_limit}</div>
              <div className="text-zinc-500">Units</div><div>{p.entity_limit}</div>
              <div className="text-zinc-500">Storage</div><div>{p.storage_limit_gb} GB</div>
              <div className="text-zinc-500">Iframe</div><div>{p.iframe_enabled ? "✓" : "—"}</div>
              <div className="text-zinc-500">Analytics</div><div>{p.analytics_enabled ? "✓" : "—"}</div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="font-display text-xl mb-5">{editing === "new" ? "New package" : "Edit package"}</div>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["name", "Name", "text"], ["currency", "Currency", "text"],
                ["monthly_price", "Monthly price", "number"], ["project_limit", "Project limit", "number"],
                ["user_limit", "User limit", "number"], ["storage_limit_gb", "Storage (GB)", "number"],
                ["scene_limit", "Scene limit", "number"], ["entity_limit", "Entity limit", "number"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="block text-xs text-zinc-700 mb-1.5">{label}</label>
                  <input data-testid={`pkg-field-${k}`} type={type} value={form[k] ?? ""}
                    onChange={(e) => setForm({ ...form, [k]: type === "number" ? Number(e.target.value) : e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900" />
                </div>
              ))}
              <label className="flex items-center gap-2 text-sm col-span-2">
                <input type="checkbox" checked={form.iframe_enabled} onChange={(e) => setForm({ ...form, iframe_enabled: e.target.checked })} /> Iframe enabled
              </label>
              <label className="flex items-center gap-2 text-sm col-span-2">
                <input type="checkbox" checked={form.analytics_enabled} onChange={(e) => setForm({ ...form, analytics_enabled: e.target.checked })} /> Analytics enabled
              </label>
              <label className="flex items-center gap-2 text-sm col-span-2">
                <input type="checkbox" checked={form.lead_form_enabled} onChange={(e) => setForm({ ...form, lead_form_enabled: e.target.checked })} /> Lead form enabled
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm">Cancel</button>
              <button onClick={save} className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid="pkg-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
